import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import {
  applyGatewayWebrtcHlsRepublish,
  fetchGatewayWebrtcStatus,
  type GatewayWebrtcStatus,
} from '../../services/gatewayWebrtcStatusService';
import { formatApiErrorMessage } from '../../utils/apiErrorMessage';

type Props = {
  poolId: number | string | undefined;
  /** 현장 시설 화면에서는 숨김 — 관제센터 관리만 */
  visible?: boolean;
};

const PoolGatewayWebrtcStatusCard: React.FC<Props> = ({ poolId, visible = true }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<GatewayWebrtcStatus | null>(null);

  const load = useCallback(async () => {
    if (!poolId) return;
    setLoading(true);
    setError(null);
    try {
      const body = await fetchGatewayWebrtcStatus(poolId);
      setStatus(body);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, 'WebRTC 상태를 불러오지 못함'));
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  const applyBypass = useCallback(async () => {
    if (!poolId) return;
    setApplying(true);
    setError(null);
    try {
      const body = await applyGatewayWebrtcHlsRepublish(poolId);
      setStatus(body.status);
    } catch (e: unknown) {
      setError(formatApiErrorMessage(e, 'HLS 우회 적용 실패'));
    } finally {
      setApplying(false);
    }
  }, [poolId]);

  useEffect(() => {
    if (!visible || !poolId) return;
    void load();
    const id = window.setInterval(() => void load(), 20000);
    return () => window.clearInterval(id);
  }, [visible, poolId, load]);

  if (!visible || !poolId) return null;

  const agent = status?.agent;
  const yesNo = (v: boolean | undefined) => (v ? '있음' : '없음');
  const cameras = status?.cameras || [];
  const tsBuf =
    status?.whip_ts_buffer_bytes ?? agent?.whip_ts_buffer_bytes ?? null;
  const formatTs = (bytes: number | null | undefined) => {
    if (bytes == null || !Number.isFinite(bytes)) return '—';
    if (bytes >= 1048576) {
      const mb = bytes / 1048576;
      return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
    }
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" color="text.secondary">
          현장 WebRTC (WHIP) · 서버 우회
        </Typography>
        <Button size="small" onClick={() => void load()} disabled={loading || applying}>
          새로고침
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void applyBypass()}
          disabled={loading || applying}
        >
          우회 지금 적용
        </Button>
        {loading || applying ? <CircularProgress size={16} /> : null}
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {status ? (
        <>
          <Chip
            size="small"
            color={status.webrtc_ready ? 'success' : 'warning'}
            label={status.webrtc_ready ? 'WebRTC 수신 가능' : 'WebRTC 미수신'}
            sx={{ mb: 1, mr: 1 }}
          />
          {(status.hls_republish_count || 0) > 0 ? (
            <Chip
              size="small"
              color="info"
              label={`HLS 우회 ${status.hls_republish_count}대`}
              sx={{ mb: 1, mr: 1 }}
            />
          ) : null}
          <Typography variant="body2" sx={{ mb: 1 }}>
            {status.summary}
          </Typography>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
            · 중앙 go2rtc: {status.central_go2rtc_available ? '기동' : '미기동'}
            <br />
            · 중앙 활성 / 슬롯: {status.central_active_streams?.length || 0} /{' '}
            {status.central_stream_slots?.length || 0}
            {(status.slot_only_count || 0) > 0
              ? ` (슬롯만 ${status.slot_only_count})`
              : ''}
            <br />
            · HLS 신선: {status.hls_fresh_count ?? 0}대 · 서버 우회 설정:{' '}
            {status.hls_republish_count ?? 0}대
            <br />
            · 루프백 HLS: {status.hls_internal_base || '—'}
            <br />
            · 현장 WHIP 주소: {agent ? yesNo(agent.whip_base_set) : '보고 없음'}
            {agent?.whip_base_host ? ` · ${agent.whip_base_host}` : ''}
            {agent?.whip_base_auto ? ' (자동)' : ''}
            <br />
            · 파이프: {agent?.edge_pipeline || '—'}
            <br />
            · WHIP용 ffmpeg: {agent ? (agent.whip_ffmpeg_ok ? 'OK' : '없음/미지원') : '—'}
            <br />
            · 현장 go2rtc: {agent ? (agent.go2rtc_local_ok ? '실행 중' : '대기/미실행') : '—'}
            <br />
            · WHIP 전송 프로세스: {agent ? (agent.whip_forwarding ? '동작 중' : '없음') : '—'}
            <br />
            · WHIP ts_buffer: {formatTs(tsBuf)}
            {tsBuf != null ? ' (DATA_GATEWAY_WHIP_TS_BUFFER)' : ' (미보고·구버전 exe)'}
            <br />
            · 원본 전송:{' '}
            {status.gateway_nvr_bypass == null
              ? '—'
              : status.gateway_nvr_bypass
                ? 'ON (copy·재인코딩 없음)'
                : 'OFF (게이트웨이 재인코딩)'}
            {' · '}
            재인코딩 폭:{' '}
            {status.gateway_nvr_bypass
              ? `미적용·저장 ${
                  status.gateway_stream_width != null
                    ? `${status.gateway_stream_width}p`
                    : '—'
                }`
              : status.gateway_stream_width != null
                ? `${status.gateway_stream_width}p`
                : '미지정'}
            {status.agent_stale ? (
              <>
                <br />· 보고 시각이 오래됨
                {status.agent_age_sec != null ? ` (${Math.round(status.agent_age_sec)}초 전)` : ''}
              </>
            ) : null}
          </Typography>
          {status.ui_guide ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {status.ui_guide}
            </Alert>
          ) : null}
          {cameras.length > 0 ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                카메라별
              </Typography>
              {cameras.map((cam) => (
                <Typography key={cam.cctv_id} variant="body2" sx={{ fontFamily: 'monospace', mb: 0.25 }}>
                  {cam.stream} · HLS {cam.hls_fresh ? 'OK' : '끊김'} ·{' '}
                  {cam.central_active ? '활성' : cam.yaml_slot ? '슬롯만' : '없음'}
                  {cam.hls_republish ? ' · 우회' : ''}
                  {cam.name ? ` · ${cam.name}` : ''}
                </Typography>
              ))}
            </Box>
          ) : null}
          {status.republish_errors && Object.keys(status.republish_errors).length > 0 ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              우회 오류 예: {Object.entries(status.republish_errors)[0][0]} —{' '}
              {Object.entries(status.republish_errors)[0][1]}
            </Alert>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            관제 실시간 영상은 업로드 HLS 를 사용. HLS→WHEP 우회는 지연·버퍼가 커서 기본 재생에 쓰지 않음.
            「우회 지금 적용」은 진단용. 본선 WebRTC 는 현장 WHIP(신버전).
          </Typography>
        </>
      ) : !loading && !error ? (
        <Typography variant="body2" color="text.secondary">
          상태 없음
        </Typography>
      ) : null}
    </Box>
  );
};

export default PoolGatewayWebrtcStatusCard;
